import { useEffect, useRef, useState } from 'react';
import { AutoComplete, Form, Input, InputNumber, Button, Space, useApp, theme, Spin } from '../ui';
import { Search, MapPin, Copy, FileDown } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useDebounce } from '../hooks/useDebounce';
import { stateCodeFromName } from '../constants/indianStates';
import { geocodeSearch, geocodeReverse } from '../lib/geocode';
import { PALETTE } from '../lib/palette';

// Leaflet's default marker images don't resolve under bundlers — use a self-contained SVG pin.
delete (L.Icon.Default.prototype as any)._getIconUrl;
const PIN = L.divIcon({
  className: '',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  html: `<svg viewBox="0 0 24 32" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill={PALETTE.sky[500]} stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});

interface FieldNames {
  lat: string;
  lng: string;
  address?: string;
  city?: string;
  pincode?: string;
  /** Optional: 2-digit GST state code field. Auto-set from the pin's state name when matchable. */
  state?: string;
}

interface Props {
  /** Names of the parent form fields this picker reads/writes. Defaults match the warehouse form. */
  fields?: Partial<FieldNames>;
  height?: number;
  searchPlaceholder?: string;
  /** When true, an empty pin shows a red "pick a location" cue (the field's own rule still enforces it). */
  required?: boolean;
  /** Map start position when no pin is set yet. Defaults to the centre of India. */
  defaultCenter?: [number, number];
}

// Keep the Leaflet view in sync when lat/lng change programmatically (search / drag).
function Recenter({ lat, lng }: { lat?: number; lng?: number }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
      map.setView([lat, lng], Math.max(map.getZoom(), 14));
    }
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Leaflet mis-measures its size inside an animated Modal — recompute once mounted.
function FixSize() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

function ClickCapture({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

/**
 * Reusable map location picker — type to get address suggestions, pick one (or click/drag the
 * pin), and it fills the parent form's latitude/longitude. Address/City/Pincode are refreshed
 * from the pin every time it moves; edit them after and they stay until the pin moves again.
 * Must be rendered inside an AntD <Form>.
 */
export function LocationPicker({
  fields,
  height = 300,
  searchPlaceholder = 'Search address, locality, plant name…',
  required = false,
  defaultCenter = [22.5, 80],
}: Props) {
  const f: FieldNames = { lat: 'latitude', lng: 'longitude', address: 'address', city: 'city', pincode: 'pincode', ...(fields ?? {}) };
  const { token } = theme.useToken();
  const { message } = useApp();
  const form = Form.useFormInstance()!; // LocationPicker is always rendered inside a Form
  const lat = Form.useWatch(f.lat, form);
  const lng = Form.useWatch(f.lng, form);

  const [q, setQ] = useState('');
  const dq = useDebounce(q, 400);
  const [options, setOptions] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const justPicked = useRef<string | null>(null);

  const latN = lat != null && lat !== '' ? Number(lat) : undefined;
  const lngN = lng != null && lng !== '' ? Number(lng) : undefined;
  const hasPin = latN != null && lngN != null && Number.isFinite(latN) && Number.isFinite(lngN);

  // Overwrite the address fields from the pin (manual edits stay until the pin moves again).
  const applyAddress = (parts: { address: string; city: string; pincode: string; state: string }) => {
    const patch: Record<string, any> = {};
    if (f.address && parts.address) patch[f.address] = parts.address;
    if (f.city && parts.city) patch[f.city] = parts.city;
    if (f.pincode && parts.pincode) patch[f.pincode] = parts.pincode;
    if (f.state) {
      const code = stateCodeFromName(parts.state);
      if (code) patch[f.state] = code; // only set when we can map the name to a GST code
    }
    if (Object.keys(patch).length) form.setFieldsValue(patch);
  };

  const reverseGeocode = async (la: number, lo: number) => {
    try {
      const res = await geocodeReverse(la, lo);
      if (res) applyAddress(res);
      else message.warning("Couldn't look up the address for that pin — please fill it in manually.");
    } catch {
      // Best-effort, but tell the user so they know to type the address manually.
      message.warning("Couldn't look up the address for that pin — please fill it in manually.");
    }
  };

  const setPin = (la: number, lo: number, reverse = true) => {
    form.setFieldsValue({ [f.lat]: Number(la.toFixed(7)), [f.lng]: Number(lo.toFixed(7)) });
    if (reverse) void reverseGeocode(la, lo);
  };

  // Manual coordinate entry — write straight to the lat/lng form fields. The map's Recenter + Marker
  // watch these same values, so the pin follows what you type. No reverse-geocode, so any address you
  // typed yourself is preserved (drag the pin or search if you want the address refreshed).
  const setCoord = (field: string, v: number | string | null) => {
    const n = v === '' || v == null ? NaN : Number(v);
    form.setFieldValue(field, Number.isFinite(n) ? n : undefined);
  };

  // As-you-type address suggestions (debounced).
  useEffect(() => {
    const term = dq.trim();
    if (justPicked.current && term === justPicked.current) return; // don't re-search the chosen text
    if (term.length < 3) { setOptions([]); return; }
    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const results = await geocodeSearch(term);
        if (!cancelled) {
          setOptions(results.map((it) => ({
            value: it.label,
            label: it.label,
            lat: it.lat,
            lon: it.lng,
            parts: { address: it.address, city: it.city, pincode: it.pincode, state: it.state },
          })));
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dq]);

  const handleSelect = (_: string, option: any) => {
    justPicked.current = option.value;
    setQ(option.value);
    setOptions([]);
    setPin(option.lat, option.lon, false);        // coords from the chosen result
    if (option.parts) applyAddress(option.parts); // address from the same result
  };

  const coords = hasPin ? `${latN!.toFixed(6)}, ${lngN!.toFixed(6)}` : null;

  return (
    <div>
      <AutoComplete
        value={q}
        options={options}
        onChange={(v) => setQ(v)}
        onSelect={handleSelect}
        style={{ width: '100%', marginBottom: 8 }}
        notFoundContent={searching ? <Spin size="small" /> : dq.trim().length >= 3 ? 'No matches — click the map instead' : null}
      >
        <Input
          prefix={<Search size={16} style={{ color: token.colorTextTertiary }} />}
          suffix={searching ? <Spin size="small" /> : null}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          allowClear
        />
      </AutoComplete>

      <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${token.colorBorderSecondary}` }}>
        <MapContainer
          center={hasPin ? [latN!, lngN!] : defaultCenter}
          zoom={hasPin ? 14 : 5}
          style={{ height, width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
          <FixSize />
          <Recenter lat={latN} lng={lngN} />
          <ClickCapture onPick={(la, lo) => setPin(la, lo)} />
          {hasPin && (
            <Marker
              position={[latN!, lngN!]}
              icon={PIN}
              draggable
              eventHandlers={{ dragend: (e) => { const p = (e.target as L.Marker).getLatLng(); setPin(p.lat, p.lng); } }}
            />
          )}
        </MapContainer>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
        <MapPin size={16} style={{ color: hasPin ? token.colorWarning : token.colorError }} />
        <span style={{ fontFamily: 'monospace', color: hasPin ? token.colorText : token.colorError }}>
          {coords ?? (required ? 'Search or click the map to drop a pin (required)' : 'Search or click the map to drop a pin')}
        </span>
        <span style={{ flex: 1 }} />
        {hasPin && (
          <Space wrap size={4}>
            <Button size="sm" icon={<Copy size={16} />}
              onClick={() => { navigator.clipboard?.writeText(coords!); message.success('Coordinates copied'); }}>
              Copy
            </Button>
            <Button size="sm" icon={<FileDown size={16} />}
              href={`https://www.google.com/maps/search/?api=1&query=${latN},${lngN}`}
              target="_blank" rel="noopener">
              Maps
            </Button>
          </Space>
        )}
      </div>

      {/* Manual coordinate entry — the pin follows what you type (kept in sync with the map both ways). */}
      {/* Side by side from `sm`; stacked on a phone, where two half-width boxes cut a 7-digit coordinate off. */}
      <div className="mb-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <InputNumber
          addonBefore="Lat" placeholder="e.g. 19.1176" aria-label="Latitude"
          min={-90} max={90} step={0.0001} controls={false} value={latN}
          onChange={(v) => setCoord(f.lat, v as any)}
        />
        <InputNumber
          addonBefore="Lng" placeholder="e.g. 73.9748" aria-label="Longitude"
          min={-180} max={180} step={0.0001} controls={false} value={lngN}
          onChange={(v) => setCoord(f.lng, v as any)}
        />
      </div>

      <div style={{ fontSize: 12, color: token.colorTextTertiary, marginBottom: 4 }}>
        Enter coordinates above or drop/drag the pin — they stay in sync. Address fills from the pin (search or drag to refresh).
      </div>
    </div>
  );
}
